import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";

import Card from "@mui/material/Card";
import Modal from "@mui/material/Modal";
import PropTypes from "prop-types";
import moment from "moment";

import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogContentText from "@mui/material/DialogContentText";
import DialogTitle from "@mui/material/DialogTitle";

import MDBox from "components/MDBox";
import MDButton from "components/MDButton";
import CardTitle from "components/CardTitle";
import { useTranslation } from "react-i18next";
import { useMaterialUIController } from "context";

import DataTable from "components/DataTable";

import { useSelector, useDispatch, connect } from "react-redux";

import { deleteProjectImage } from "store/actions/mainUserActions";
import { getAllAmenities, updateAmanity, removeAmenities } from "store/actions/amenitiesActions";
import AmenityForm from "./AmenityForm";
import amenityUtils from "./amenityUtils";

const { formatTimeToFormatedString } = amenityUtils;

function CellDate({ value }) {
  return <span>{value ? moment(value).format("DD/MM/YYYY HH:mm") : ""}</span>;
}
CellDate.propTypes = { value: PropTypes.number };
CellDate.defaultProps = { value: 0 };

function CellStartEndTime({ value }) {
  return <span>{value || 0}</span>;
}
CellStartEndTime.propTypes = { value: PropTypes.string };
CellStartEndTime.defaultProps = { value: "" };

function CellDurationTime({ value }) {
  return <span>{`${value} min`}</span>;
}
CellDurationTime.propTypes = { value: PropTypes.number };
CellDurationTime.defaultProps = { value: 0 };

function CellFormatedTime({ value }) {
  return <span>{formatTimeToFormatedString(value)}</span>;
}
CellFormatedTime.propTypes = {
  value: PropTypes.number.isRequired,
};

function AlertDialog({ open, handleClose, handleAgree }) {
  const [controller] = useMaterialUIController();
  const { darkMode } = controller;
  const { t } = useTranslation();

  return (
    <Dialog open={open} onClose={handleClose}>
      <MDBox
        sx={({ palette: { white, background } }) => ({
          backgroundColor: darkMode ? background.sidenav : white.main,
        })}
      >
        <DialogTitle>{t("confirmation")}</DialogTitle>
        <DialogContent>
          <DialogContentText>{t("stepNotUndo")}</DialogContentText>
        </DialogContent>
        <DialogActions>
          <MDButton onClick={handleClose}>{t("cancel")}</MDButton>
          <MDButton onClick={handleAgree} autoFocus>
            {t("OK")}
          </MDButton>
        </DialogActions>
      </MDBox>
    </Dialog>
  );
}

AlertDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  handleClose: PropTypes.func.isRequired,
  handleAgree: PropTypes.func,
};
AlertDialog.defaultProps = { handleAgree: () => {} };

function Amenities({
  amenities,
  projectId,
  mainUserId,
  actGetAmenities,
  actUpdateAmenity,
  actRemoveAmenities,
}) {
  const [open, setOpen] = useState(false);
  const [openConfirm, setOpenConfirm] = useState(false);
  const [uids, setUids] = useState([]);
  const [actionType, setActionType] = useState("");

  const { t } = useTranslation();
  const navigate = useNavigate();
  const dispatch = useDispatch();

  useEffect(() => {
    actGetAmenities();
  }, []);

  const columns = () => [
    { Header: t("name"), accessor: "name" },
    { Header: t("amenityDuration"), accessor: "amenityDuration", Cell: CellDurationTime },
    { Header: t("createdAt"), accessor: "createdAt", Cell: CellDate },
    { Header: t("cost"), accessor: "cost" },
    { Header: t("currency"), accessor: "currency" },
    { Header: t("startTime"), accessor: "startTime", Cell: CellStartEndTime },
    { Header: t("endTime"), accessor: "endTime", Cell: CellStartEndTime },
    { Header: t("minPreviousTime"), accessor: "minPreviousTime", Cell: CellFormatedTime },
    { Header: t("minCancelTime"), accessor: "minCancelTime", Cell: CellFormatedTime },
  ];

  const remove = (data) => {
    setUids(data.map((d) => d.original));
    setActionType("remove");
    setOpenConfirm(true);
  };

  const onSubmit = (data) => {
    if (actionType === "edit" || actionType === "add")
      actUpdateAmenity({
        mainUserId,
        projectId,
        amenityData: data,
      });

    setOpen(false);
  };

  const deleteImagePromise = (imageUid) =>
    new Promise((resolve) => {
      dispatch(
        deleteProjectImage({
          imageUid,
          projectId,
          cb: () => resolve(true),
        })
      );
    });

  const confirm = async () => {
    if (actionType !== "remove") return;

    const selected = Array.isArray(uids) ? uids : [];
    const imageUids = selected.map((r) => r?.imageUid).filter(Boolean);

    await Promise.all(imageUids.map(deleteImagePromise));

    actRemoveAmenities({
      mainUserId,
      projectId,
      amenities: selected,
    });

    setOpenConfirm(false);
    setUids([]);
    setActionType("");
  };

  const add = () => {
    setActionType("add");
    setOpen(true);
  };

  const edit = (indexes) => {
    setUids(indexes.map((i) => i.original.uid));
    setActionType("edit");
    setOpen(true);
  };

  const menuConfig = useSelector((state) => state.mainUserReducer.customApp.menuConfig);
  const reservationsLabel = t(
    menuConfig?.amenities?.label ?? menuConfig?.amenities?.defaultLabel ?? "amenities"
  );

  return (
    <Card>
      <AlertDialog
        open={openConfirm}
        handleClose={() => setOpenConfirm(false)}
        handleAgree={confirm}
      />

      <Modal open={open} onClose={() => setOpen(false)} style={{ overflow: "scroll" }}>
        <MDBox
          mt={3}
          sx={{
            width: { xs: "90vw", md: "65vw" },
            position: "absolute",
            left: "50%",
            transform: "translateX(-50%)",
          }}
        >
          <AmenityForm
            onSubmit={onSubmit}
            actionType={actionType}
            uids={uids}
            projectId={projectId}
          />
        </MDBox>
      </Modal>

      <CardTitle title={reservationsLabel} icon="access_time" bgColor="warning" />

      <DataTable
        table={{ columns: columns(), rows: Object.values(amenities || {}) }}
        canSearch
        canSelect
        canEdit
        canAdd
        canActuate
        action={(data) => {
          const uid = data?.original?.uid;
          if (uid) navigate(`/projects/${projectId}/amenity/${uid}`);
        }}
        actionIcon="arrow_forward_ios"
        actionColor="info"
        add={add}
        edit={edit}
        remove={remove}
      />
    </Card>
  );
}

Amenities.propTypes = {
  amenities: PropTypes.object,
  projectId: PropTypes.string,
  mainUserId: PropTypes.string.isRequired,
  actGetAmenities: PropTypes.func.isRequired,
  actUpdateAmenity: PropTypes.func.isRequired,
  actRemoveAmenities: PropTypes.func.isRequired,
};

Amenities.defaultProps = {
  amenities: {},
  projectId: "",
};

const mapStateToProps = (state, ownProps) => {
  const { amenitiesReducer, projectsReducer } = state;
  const { projectId } = ownProps;

  const allAmenties = amenitiesReducer?.amenities || {};
  const project = projectsReducer ? projectsReducer[projectId] : {};
  const amenitiesIds = project?.amenities || {};

  const projectAmenities = {};

  Object.keys(amenitiesIds).forEach((id) => {
    if (allAmenties[id]) {
      projectAmenities[id] = allAmenties[id];
    }
  });

  return {
    amenities: projectAmenities,
  };
};

const mapDispatchToProps = (dispatch) => ({
  actGetAmenities: () => dispatch(getAllAmenities()),
  actUpdateAmenity: (data) => dispatch(updateAmanity(data)),
  actRemoveAmenities: (data) => dispatch(removeAmenities(data)),
});

export default connect(mapStateToProps, mapDispatchToProps)(Amenities);
